import { Button } from '@flywire/headlessui-tailwindcss';
import { useFormState, useValidate } from '@flywire/react-hooks';
import { signupUser } from 'actions/entities/user/user';
import { trackUserLoginSignup } from 'actions/tracker/tracker';
import { newNotification } from 'actions/ui/ui';
import { Fetching } from 'components/Fetching/Fetching';
import { Heading } from 'components/Heading/Heading';
import type { WithFocusOnErrorProps } from 'components/HOC/WithFocusOnError/WithFocusOnError';
import { WithFocusOnError } from 'components/HOC/WithFocusOnError/WithFocusOnError';
import { TextInput } from 'components/input/TextInput/TextInput';
import { PasswordInput } from 'components/PasswordInput/PasswordInput';
import { RequiredFieldLabel } from 'components/RequiredFieldLabel/RequiredFieldLabel';
import { SocialSignIn } from 'components/SocialSignIn/SocialSignIn';
import {
  Captcha,
  type CaptchaRef,
} from 'components/thirdParty/Captcha/Captcha';
import {
  SENDER_EMAIL_FIELD,
  SENDER_FIRST_NAME_FIELD,
  SENDER_LAST_NAME_FIELD,
} from 'constants/fields';
import type { HttpError } from 'constants/http';
import { HTTPStatuses } from 'constants/http';
import {
  UserLoginSource,
  UserLoginType,
} from 'contexts/LoginContext/LoginContext';
import { useLoginOrigin } from 'hooks/useLoginOrigin/useLoginOrigin';
import type React from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getFieldValue } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { isHttpErrorWithParsedBody } from 'utils/errors/errors';
import { useTranslations } from 'utils/translations/useTranslations';
import { TermsAndConditions } from './components/TermsAndConditions/TermsAndConditions';
import { getConstraints } from './getConstraints';
import './UserSignup.scss';

const WAIT_TIME = 125;

export type UserSignupProps = WithFocusOnErrorProps & {
  firstInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  handleLoginClick?: () => void;
  onSignUpSuccess?: () => void;
  header?: React.ReactNode;
  hideSocialLogin?: boolean;
};

function UserSignup({
  focusOnFirstError,
  handleLoginClick,
  onSignUpSuccess,
  firstInputRef,
  header,
  hideSocialLogin,
}: UserSignupProps) {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const captchaRef = useRef<CaptchaRef>(null);
  const initialEmail = useSelector((state: RootState) =>
    getFieldValue(state, SENDER_EMAIL_FIELD),
  );
  const initialFirstName = useSelector((state: RootState) =>
    getFieldValue(state, SENDER_FIRST_NAME_FIELD),
  );
  const initialLastName = useSelector((state: RootState) =>
    getFieldValue(state, SENDER_LAST_NAME_FIELD),
  );
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [serverError, setServerErrors] = useState<Record<string, string>>({});
  const constraints = useMemo(() => getConstraints({ i18n }), [i18n]);
  const { values, update, dirtyFields } = useFormState({
    terms: false,
    firstName: initialFirstName,
    lastName: initialLastName,
    email: initialEmail,
  });

  const {
    isValid,
    errors: clientErrors,
    validate: clientValidate,
    // @ts-expect-error not properly typed
  } = useValidate(values, constraints);

  const { loginOrigin } = useLoginOrigin();

  function handleOnChange(name: string, value?: string | boolean | number) {
    update(name, value);
  }

  function moveFocusToFirstError() {
    setTimeout(focusOnFirstError, WAIT_TIME);
  }

  async function handleOnSubmit(evt: FormEvent) {
    evt.preventDefault();

    setIsFormSubmitted(true);
    clientValidate();

    if (!isValid) {
      return moveFocusToFirstError();
    }

    if (captchaRef.current?.isEnabled()) {
      captchaRef.current?.execute();
    } else {
      handleSignup();
    }
  }

  const signUpSuccess = () => {
    onSignUpSuccess?.();
    resetCaptcha();
  };

  const resetCaptcha = () => {
    captchaRef.current?.reset();
  };

  const handleSignup = async (captchaToken?: string) => {
    const email = values.email as string;
    const password = values.password as string;
    const firstName = values.firstName as string;
    const lastName = values.lastName as string;

    try {
      await dispatch(
        signupUser({ firstName, lastName, email, password, captchaToken }),
      );

      signUpSuccess();
      dispatch(
        trackUserLoginSignup(
          UserLoginType.SIGNUP,
          UserLoginSource.EMAIL,
          loginOrigin,
        ),
      );
    } catch (error) {
      if (isHttpErrorWithParsedBody(error)) {
        const { parsedBody } = error as HttpError;
        const errors = parsedBody?.errors
          ? Array.isArray(parsedBody.errors)
            ? parsedBody.errors
            : []
          : [];

        if (errors.length) {
          const parsedErrors = errors?.reduce(
            (
              acc: Record<string, string>,
              value: { param: string; message: string },
            ) => ({ ...acc, [value.param]: [value.message] }),
            {},
          );

          setServerErrors(parsedErrors);
          moveFocusToFirstError();
          captchaRef.current?.reset();
        } else {
          handleError();
        }
      } else {
        handleError();
      }
    }
  };

  const handleSignupWithCaptcha = async (token: string) => {
    if (token) {
      handleSignup(token);
    } else {
      handleError();
    }
  };

  const handleError = () => {
    dispatch(
      newNotification({
        message: 'userAccessModal.signup.createUser.error',
        type: 'error',
      }),
    );
    resetCaptcha();
  };

  function errorOn(attr: string) {
    return (
      (dirtyFields[attr] || isFormSubmitted) &&
      (clientErrors[attr]?.[0] || serverError[attr]?.[0])
    );
  }

  const handleSocialSignInError = (err: unknown) => {
    resetCaptcha();

    if (isHttpErrorWithParsedBody(err)) {
      const { status } = err as HttpError;
      if (status === HTTPStatuses.UNAUTHORIZED) {
        const message = 'notifications.social_signin_unauthorized';

        dispatch(
          newNotification({
            message,
            type: 'error',
          }),
        );
      }
    }
  };

  return (
    <>
      <Fetching entity="user" />

      {header ? (
        header
      ) : (
        <Heading
          as="h2"
          size="large"
          className="textAlign-center marginBottom-xxl"
          id="Page-title"
        >
          {i18n.t('userSignup.title')}
        </Heading>
      )}

      {!hideSocialLogin && (
        <SocialSignIn
          onSuccess={signUpSuccess}
          onError={handleSocialSignInError}
          isLogin={false}
        />
      )}

      <form className="UserSignup" onSubmit={handleOnSubmit} noValidate>
        <RequiredFieldLabel />
        <div className="flex flex-col gap-8">
          <TextInput
            autoFocus
            data-testid="firstName"
            error={errorOn('firstName')}
            floatingLabel
            label={i18n.t('userSignup.firstName.label') as string}
            name="firstName"
            onChange={handleOnChange}
            required
            value={values?.firstName as string}
            forwardRef={firstInputRef}
          />
          <TextInput
            data-testid="lastName"
            error={errorOn('lastName')}
            floatingLabel
            label={i18n.t('userSignup.lastName.label') as string}
            name="lastName"
            onChange={handleOnChange}
            required
            value={values?.lastName as string}
          />
          <TextInput
            data-testid="email"
            error={errorOn('email')}
            floatingLabel
            label={i18n.t('userSignup.email.label') as string}
            name="email"
            onChange={handleOnChange}
            required
            value={values?.email as string}
          />
          <TextInput
            data-testid="confirmationEmail"
            error={errorOn('confirmationEmail')}
            floatingLabel
            label={i18n.t('userSignup.repeat_email.label') as string}
            name="confirmationEmail"
            onChange={handleOnChange}
            required
            value={values?.confirmationEmail as string}
          />
          <div>
            <PasswordInput
              data-testid="password"
              error={errorOn('password')}
              floatingLabel
              label={i18n.t('userSignup.password.label')}
              name="password"
              onChange={handleOnChange}
              required
            />
            <input
              data-testid="repeatPassword"
              required
              name="repeatPassword"
              onChange={(evt: ChangeEvent<HTMLInputElement>) =>
                handleOnChange(evt.target.name, evt.target.checked)
              }
              type="password"
              className="Input--hidden"
            />
          </div>
          <TermsAndConditions
            error={errorOn('terms')}
            name="terms"
            onChange={(evt: ChangeEvent<HTMLInputElement>) =>
              handleOnChange(evt.target.name, evt.target.checked)
            }
            required
            checked={(values as { terms: boolean } | undefined)?.terms}
          />
        </div>
        <Button
          tone="primary"
          size="full"
          type="submit"
          className="mt-10"
          data-testid="signUpButton"
        >
          {i18n.t('userSignup.submit')}
        </Button>
      </form>
      <div className="UserSignup-question">
        {i18n.t('userSignup.already.user')}
      </div>
      <div className="UserSignup-link">
        <button
          onClick={handleLoginClick}
          type="button"
          role="link"
          data-testid="login"
        >
          {i18n.t('userSignup.login.link')}
        </button>
      </div>
      <Captcha
        ref={captchaRef}
        onVerify={handleSignupWithCaptcha}
        onClose={resetCaptcha}
        onError={handleError}
      />
    </>
  );
}
const UserSignupWithFocustOnError = WithFocusOnError(UserSignup);

export { UserSignupWithFocustOnError as UserSignup };
